/**
 * SFDA-858: /welcome and /callback with REACT_APP_AUTH_PROVIDER=zitadel.
 * Signing in happens on our login page (apps/login, via Zitadel); these pages only send
 * people there and take them back.
 */
import { Box, Button, CircularProgress, CssBaseline, Fade, Typography } from '@mui/material';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { PageTitle } from '../../../components/PageTitle';
import { useAuth } from '../../../contexts/auth/useAuth';
import {
  NoAccountError,
  useZitadel,
} from '../../../contexts/auth/zitadelContext';
import { safeReturnTo } from '../welcome/Welcome';

/** Hidden on screen, still read by screen readers. */
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/** A centred card with one heading that takes focus, so screen readers start there. */
function Shell({
  heading,
  children,
}: {
  heading: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [heading]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-light-greyish-blue)',
      }}
    >
      <CssBaseline />
      <PageTitle title={t('auth.welcome.pageTitle')} />
      <Box
        component="main"
        id="main-content"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 16,
          py: 32,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            p: 32,
            maxWidth: 480,
            width: '100%',
            backgroundColor: 'var(--color-white, #fff)',
            borderRadius: 2,
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            ref={headingRef}
            tabIndex={-1}
            sx={{ outline: 'none' }}
          >
            {heading}
          </Typography>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

/**
 * A step with nothing to do but wait (the session check, on to the sign-in page, back from it): a
 * spinner on the login page's background, so the hand-over to apps/login doesn't flash a card. The
 * words are for screen readers only. The spinner fades in after a moment, so a fast redirect shows
 * nothing at all. With `heading`, an h1 (hidden) takes focus and the tab is titled "Sign in".
 */
export function ZitadelLoading({
  label,
  heading,
}: {
  label?: ReactNode;
  heading?: ReactNode;
}) {
  const { t } = useTranslation();
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, [heading]);
  const status = label ?? t('auth.zitadel.loading');

  return (
    <Box
      component="main"
      id="main-content"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--color-light-greyish-blue)',
      }}
    >
      <CssBaseline />
      {heading ? (
        <>
          <PageTitle title={t('auth.welcome.pageTitle')} />
          <Typography
            component="h1"
            ref={headingRef}
            tabIndex={-1}
            sx={visuallyHidden}
          >
            {heading}
          </Typography>
          <Box role="status" sx={visuallyHidden}>
            {status}
          </Box>
        </>
      ) : (
        // The page's only text, so also its h1 (axe page-has-heading-one), inside the status region.
        <Box role="status" sx={visuallyHidden}>
          <Typography component="h1">{status}</Typography>
        </Box>
      )}
      <Fade in style={{ transitionDelay: '400ms' }}>
        <CircularProgress
          aria-hidden="true"
          sx={{ color: 'var(--color-scifree-teal)' }}
        />
      </Fade>
    </Box>
  );
}

export function ZitadelWelcome() {
  const { t } = useTranslation();
  const { isAuthenticated, sessionExpired } = useAuth();
  const { signIn } = useZitadel();
  const [searchParams] = useSearchParams();
  const returnTo = safeReturnTo(searchParams.get('returnTo'));
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  const go = () => {
    setFailed(false);
    signIn(returnTo).catch(() => setFailed(true));
  };

  // Straight on to sign-in, except after an expired session: say so first.
  useEffect(() => {
    if (isAuthenticated || sessionExpired || started.current) return;
    started.current = true;
    go();
  }, [isAuthenticated, sessionExpired]);

  if (isAuthenticated) return <Navigate to={returnTo} replace />;

  if (sessionExpired || failed) {
    return (
      <Shell
        heading={
          failed
            ? t('auth.zitadel.unreachable')
            : t('auth.welcome.errors.session_expired')
        }
      >
        <Button variant="primary" onClick={go}>
          {t('auth.signIn')}
        </Button>
      </Shell>
    );
  }

  return (
    <ZitadelLoading
      heading={t('auth.welcome.pageTitle')}
      label={t('auth.zitadel.redirecting')}
    />
  );
}

export function ZitadelCallback() {
  const { t } = useTranslation();
  const { completeSignIn } = useZitadel();
  const navigate = useNavigate();
  const [error, setError] = useState<'noAccess' | 'failed' | null>(null);
  const started = useRef(false);

  useEffect(() => {
    // The code can be exchanged once; StrictMode runs effects twice in dev.
    if (started.current) return;
    started.current = true;
    completeSignIn()
      .then((returnTo) => navigate(safeReturnTo(returnTo), { replace: true }))
      .catch((err) =>
        setError(err instanceof NoAccountError ? 'noAccess' : 'failed')
      );
  }, [completeSignIn, navigate]);

  if (error === 'noAccess') {
    return (
      <Shell heading={t('auth.welcome.noAccess.heading')}>
        <Typography>{t('auth.welcome.noAccess.message')}</Typography>
        <Button variant="primary" onClick={() => navigate('/welcome')}>
          {t('auth.welcome.noAccess.differentEmail')}
        </Button>
      </Shell>
    );
  }
  if (error === 'failed') {
    return (
      <Shell heading={t('auth.zitadel.failed')}>
        <Button variant="primary" onClick={() => navigate('/welcome')}>
          {t('auth.zitadel.retry')}
        </Button>
      </Shell>
    );
  }
  return (
    <ZitadelLoading
      heading={t('auth.welcome.pageTitle')}
      label={t('auth.zitadel.signingIn')}
    />
  );
}
